import { Component } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { CrmEventAttendingStatus } from "@enums/crmEventAttendingStatus.enum";
import { CrmEventType } from "@enums/crmEventType.enum";
import { InteractionType } from "@enums/interactionType.enum";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { CrmEvent } from "@interfaces/models/crmEvent.interface";
import { CrmEventAttendee } from "@interfaces/models/crmEventAttendee.interface";
import { CrmInteraction } from "@interfaces/models/crmInteraction.interface";
import { UserProfile } from "@interfaces/userProfile.interface";
import { AuthService } from "@services/auth.service";
import { CrmEventAttendeeService } from "@services/crmEventAttendee.service";
import { CrmEventService } from "@services/crmEvent.service";
import { CrmInteractionService } from "@services/crmInteraction.service";
import { UserProfileService } from "@services/userProfile.service";
import { ToastService } from "angular-toastify";

@Component({
	moduleId: module.id,
	selector: "crm-events-id",
	styleUrls: ["crmEvents-id.css"],
	templateUrl: "crmEvents-id.html"
})
export class CrmEventIdAdminComponent {
	attendees: CrmEventAttendee[] = [];
	attendingStatuses: any[] = [];
	eventTypes: any[] = [];
	headerPrimaryButtons: AdminHeaderButton[] = [];
	interactionTypes: any[] = [];
	interactions: CrmInteraction[] = [];
	item: CrmEvent = new CrmEvent();
	itemId = 0;
	itemType = "CRM Event";
	popupAddButtonOptions: any;
	popupAttendeeUserId: number | null = null;
	popupCloseButtonOptions: any = { onClick: () => this.closePopup(), text: "Close" };
	popupInteraction: CrmInteraction = new CrmInteraction();
	popupTitle = "";
	popupType = "";
	popupVisible = false;
	readOnly = true;
	returnUrl: string = location.href.split("/")[3] + "/" + location.href.split("/")[4];
	title: string = "View " + this.itemType;
	userProfile: UserProfile = new UserProfile();
	userProfiles: UserProfile[] = [];

	constructor(private router: Router, private toastService: ToastService, private route: ActivatedRoute, private itemService: CrmEventService, private crmEventAttendeeService: CrmEventAttendeeService, private crmInteractionService: CrmInteractionService, private userProfileService: UserProfileService, private authService: AuthService) {
		this.eventTypes = GetPropertiesFromEnum(CrmEventType);
		this.attendingStatuses = GetPropertiesFromEnum(CrmEventAttendingStatus);
		this.interactionTypes = GetPropertiesFromEnum(InteractionType);
		this.buildButtons("view");
		this.deleteAttendee = this.deleteAttendee.bind(this);
		this.route.params.subscribe((params) => {
			if (params.id !== "create") {
				if (params.id.split("_").length > 1) {
					this.itemId = params.id.split("_")[0];
					this.getItem(this.itemId);
					this.edit();
				} else {
					this.itemId = params.id;
					this.getItem(params.id);
				}
			} else {
				this.create();
			}
		});
		this.getUserProfile();
		this.getUserProfiles();
	}

	addAttendeePopup() {
		this.popupVisible = true;
		this.popupAttendeeUserId = null;
		this.popupTitle = "Add Attendee";
		this.popupType = "Attendee";
		this.popupAddButtonOptions = { onClick: () => this.saveAttendee(), text: "Add Attendee" };
	}

	addInteractionPopup() {
		this.popupVisible = true;
		this.popupInteraction = new CrmInteraction();
		this.popupInteraction.interactionDate = new Date();
		this.popupTitle = "Add Interaction";
		this.popupType = "Interaction";
		this.popupAddButtonOptions = { onClick: () => this.saveInteraction(), text: "Add Interaction" };
	}

	buildButtons(mode: string) {
		if (mode === "edit") {
			this.headerPrimaryButtons = [
				{ method: "updateItem", text: "Save Changes" },
				{ method: "cancelEditing", text: "View Mode" },
				{ method: "close", text: "Close" }
			];
		} else if (mode === "create") {
			this.headerPrimaryButtons = [
				{ method: "createItem", text: "Save New Event" },
				{ method: "close", text: "Close" }
			];
		} else {
			this.headerPrimaryButtons = [
				{ method: "edit", text: "Edit" },
				{ method: "close", text: "Close" }
			];
		}
	}

	cancelEditing() {
		this.readOnly = true;
		this.buildButtons("view");
		this.title = "View " + this.itemType;
		this.getItem(this.itemId);
	}

	close() { this.router.navigate([this.returnUrl]); }

	closePopup() { this.popupVisible = false; }

	create() {
		this.readOnly = false;
		this.title = "New " + this.itemType;
		this.buildButtons("create");
	}

	createItem() {
		this.itemService.createSingle(this.item).subscribe(
			(res: any) => { this.toastService.success("Event created!"); this.router.navigate([`${this.returnUrl}/${res.response.id}`]); },
			(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
		);
	}

	deleteAttendee(e: any) {
		if (confirm("Remove this attendee?")) {
			this.crmEventAttendeeService.deleteSingleById(e.row.data.id).subscribe(
				() => { this.toastService.success("Attendee removed!"); this.getAttendees(this.itemId); },
				(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
			);
		}
	}

	edit() {
		this.readOnly = false;
		this.title = "Edit " + this.itemType;
		this.buildButtons("edit");
	}

	getAttendees(eventId: any) {
		this.crmEventAttendeeService.getByEventId(eventId).subscribe(
			(res: any) => { this.attendees = res.response || []; },
			(err) => { console.log(err); }
		);
	}

	getInteractions(eventId: any) {
		this.crmInteractionService.getByEventId(eventId).subscribe(
			(res: any) => { this.interactions = res.response || []; },
			(err) => { console.log(err); }
		);
	}

	getItem(id: any) {
		this.itemService.getSingleById(id).subscribe(
			(res: any) => {
				this.item = res.response;
				this.getAttendees(id);
				this.getInteractions(id);
			},
			(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
		);
	}

	getUserName(userProfileId: number) {
		const user = this.userProfiles.find(u => u.id === userProfileId);
		return user ? user.fullName : userProfileId;
	}

	getUserProfile() {
		this.authService.getCurrentUserProfile().subscribe(
			(res: any) => { this.userProfile = res.response; },
			(err) => { console.log(err); }
		);
	}

	getUserProfiles() {
		this.userProfileService.getAll().subscribe(
			(res: any) => { this.userProfiles = res.response.items; },
			(err) => { console.log(err); }
		);
	}

	headerButtonClick(method: any) {
		// @ts-ignore
		if (this[method]) this[method]();
	}

	saveAttendee() {
		const attendee = new CrmEventAttendee();
		attendee.crmEventId = this.item.id;
		attendee.userProfileId = this.popupAttendeeUserId!;
		this.crmEventAttendeeService.createSingle(attendee).subscribe(
			() => { this.closePopup(); this.getAttendees(this.itemId); this.toastService.success("Attendee added!"); },
			(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
		);
	}

	saveInteraction() {
		this.popupInteraction.crmEventId = this.item.id;
		this.popupInteraction.instigatorId = this.userProfile.id;
		this.crmInteractionService.createSingle(this.popupInteraction).subscribe(
			() => { this.closePopup(); this.getInteractions(this.itemId); this.toastService.success("Interaction added!"); },
			(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
		);
	}

	updateItem() {
		this.itemService.updateSingleById(this.item.id, this.item).subscribe(
			() => { this.toastService.success("Event updated!"); this.cancelEditing(); },
			(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
		);
	}
}
